import { useState, useEffect, useCallback } from 'react';

import {
  getAllGoals,
  getGoalById,
  createGoal,
  updateGoal,
  deleteGoal,
  updateGoalProgress,
  getGoalStats,
  getUserCompanyDepartments,
  getAvailableOwners,
  getGoalTree,
  GoalFilterOptions,
  GoalStatsView,
  GoalView,
  GoalOwnerView,
  DepartmentBasicView,
  GoalTreeView,
} from '../../../api/PreformanceManagement/Goals';

import type { Goal, Filters, ExpandedSections, Position } from './types';
import {
  convertGoalViewToGoal,
  convertGoalToGoalInput,
  mapBackendLevelToFrontend,
  mapBackendStatusToFrontend,
} from './utils';

const MAX_VISIBLE_PER_SECTION = 50;

interface ApiError {
  response?: { status?: number; data?: { errors?: Array<{ message?: string }> } };
  message?: string;
}

function getErrorMessage(err: unknown, fallback: string): string {
  const apiErr = err as ApiError;
  return apiErr?.response?.data?.errors?.[0]?.message
    || (err instanceof Error ? err.message : '')
    || fallback;
}

function getHttpStatus(err: unknown): number | undefined {
  return (err as ApiError)?.response?.status;
}

export function useGoals(userId: string | undefined, t: (key: string, options?: Record<string, unknown>) => string) {
  // Add fallback for userId using localStorage
  const currentUserId = userId || localStorage.getItem('currentUserId') || '';

  // Save userId to localStorage when it's available
  useEffect(() => {
    if (userId) {
      localStorage.setItem('currentUserId', userId);
    }
  }, [userId]);

  // ----------------------
  // STATE
  // ----------------------
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(true);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'view' | 'create' | 'edit'>('view');
  const [hoveredGoal, setHoveredGoal] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    company: true,
    team: true,
    individual: true
  });
  const [viewType, setViewType] = useState<'map' | 'list'>('map');
  const [filters, setFilters] = useState<Filters>({
    search: '',
    department: 'all',
    user: 'all',
    dueDate: 'all',
    status: [],
    levels: []
  });

  // API-related state
  const [goals, setGoals] = useState<Goal[]>([]);
  const [treeData, setTreeData] = useState<GoalTreeView | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<GoalStatsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  // Departments & owners state
  const [departments, setDepartments] = useState<DepartmentBasicView[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState<boolean>(false);
  const [availableOwners, setAvailableOwners] = useState<GoalOwnerView[]>([]);
  const [loadingOwners, setLoadingOwners] = useState<boolean>(false);

  // Virtual scrolling state
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({
    company: MAX_VISIBLE_PER_SECTION,
    team: MAX_VISIBLE_PER_SECTION,
    individual: MAX_VISIBLE_PER_SECTION,
  });

  // ----------------------
  // EFFECTS
  // ----------------------

  // Fetch departments and available owners on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const depts = await getUserCompanyDepartments();
        setDepartments(depts || []);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    const fetchAvailableOwners = async () => {
      try {
        setLoadingOwners(true);
        const owners = await getAvailableOwners();
        setAvailableOwners(owners || []);
      } catch (error) {
        console.error('Failed to fetch available owners:', error);
        setAvailableOwners([]);
      } finally {
        setLoadingOwners(false);
      }
    };

    fetchDepartments();
    fetchAvailableOwners();
  }, []);

  // Fetch goals and tree data on mount and when filters change
  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const filterOptions: GoalFilterOptions = {
          department: filters.department !== 'all' ? filters.department : undefined,
          status: filters.status.length > 0 ? filters.status.join(',') : undefined,
          level: filters.levels.length > 0 ? filters.levels.join(',') : undefined,
          priority: filters.priority && filters.priority.length > 0 ? filters.priority.join(',') : undefined,
          tags: filters.tags && filters.tags.length > 0 ? filters.tags.join(',') : undefined,
          searchTerm: filters.search || undefined,
        };

        // Fetch flat goals and tree data in parallel
        const [goalsResult, treeResult, statsResult] = await Promise.allSettled([
          getAllGoals(filterOptions),
          getGoalTree(),
          getGoalStats(filterOptions),
        ]);

        // Process flat goals
        if (goalsResult.status === 'fulfilled' && goalsResult.value?.length > 0) {
          const convertedGoals = goalsResult.value.map(convertGoalViewToGoal);
          setGoals(convertedGoals);
        } else if (goalsResult.status === 'rejected') {
          const apiError = goalsResult.reason;
          setGoals([]);
          if (apiError?.response?.status === 401) {
            setError(t('Authentication required. Please log in.'));
          } else if (apiError?.response?.status === 403) {
            setError(t('You do not have permission to view goals.'));
          } else {
            setError(t('Could not fetch goals from server.'));
          }
        } else {
          setGoals([]);
        }

        // Process tree data
        if (treeResult.status === 'fulfilled' && treeResult.value) {
          setTreeData(treeResult.value);
          // Auto-expand company-level goals on first load
          if (expandedNodes.size === 0 && treeResult.value.children) {
            const initialExpanded = new Set<string>();
            treeResult.value.children.forEach(child => {
              if (child.goal?.id) initialExpanded.add(child.goal.id);
            });
            setExpandedNodes(initialExpanded);
          }
        }

        // Process stats
        if (statsResult.status === 'fulfilled') {
          setStats(statsResult.value);
        } else {
          const goalList = goalsResult.status === 'fulfilled' && goalsResult.value
            ? goalsResult.value.map(convertGoalViewToGoal) : [];
          setStats({
            totalGoals: goalList.length,
            onTrack: goalList.filter(g => g.status === 'on-track').length,
            atRisk: goalList.filter(g => g.status === 'at-risk').length,
            completed: goalList.filter(g => g.status === 'completed').length,
            averageProgress: goalList.length > 0
              ? Math.round(goalList.reduce((sum, g) => sum + g.progress, 0) / goalList.length)
              : 0,
          });
        }
      } catch (err) {
        console.error('Error in fetchGoals:', err);
        setError(err instanceof Error ? err.message : t('An error occurred while fetching goals.'));
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, refetchTrigger]);

  // ----------------------
  // HELPERS
  // ----------------------

  // Helper: toggle expand/collapse for tree node
  const toggleNodeExpanded = (goalId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  // ----------------------
  // COMPUTED
  // ----------------------

  const filteredGoals = goals.filter((goal) => {
    if (filters.search &&
        !goal.title.toLowerCase().includes(filters.search.toLowerCase()) &&
        !goal.description.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }

    if (filters.department && filters.department !== 'all' && goal.department !== filters.department) {
      return false;
    }

    if (filters.user && filters.user !== 'all' && goal.owner !== filters.user) {
      return false;
    }

    if (filters.status && filters.status.length > 0 && !filters.status.includes(goal.status)) {
      return false;
    }

    if (filters.levels && filters.levels.length > 0 && !filters.levels.includes(goal.level)) {
      return false;
    }

    if (filters.hasBlockers && !goal.hasActiveBlockers) {
      return false;
    }

    return true;
  });

  const getStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'on-track': t('On Track'),
      'at-risk': t('At Risk'),
      'completed': t('Completed'),
      'overdue': t('Overdue'),
    };
    return statusLabels[status] || status;
  };

  // ----------------------
  // TREE POSITION HELPERS
  // ----------------------

  // Check if a goal passes current filters
  const isGoalVisible = (goalView: GoalView): boolean => {
    const level = mapBackendLevelToFrontend(goalView.level as string | number);
    const status = mapBackendStatusToFrontend(goalView.status);
    const title = goalView.title || '';
    const desc = goalView.description || '';
    const owner = goalView.owner?.name || '';
    const dept = goalView.department?.name || '';

    if (filters.search &&
      !title.toLowerCase().includes(filters.search.toLowerCase()) &&
      !desc.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.department && filters.department !== 'all' && dept !== filters.department) {
      return false;
    }
    if (filters.user && filters.user !== 'all' && owner !== filters.user) {
      return false;
    }
    if (filters.status?.length > 0 && !filters.status.includes(status)) {
      return false;
    }
    if (filters.levels?.length > 0 && !filters.levels.includes(level)) {
      return false;
    }
    return true;
  };

  // Build tree position map: calculates x/y positions for hierarchical tree layout
  const buildTreePositionMap = (): Map<string, Position> => {
    const posMap = new Map<string, Position>();
    if (!treeData?.children?.length) {
      // Fallback: use flat goals grouped by level
      const containerWidth = 1100;
      const levels: Array<'company' | 'team' | 'individual'> = ['company', 'team', 'individual'];
      const levelConfig = {
        company: { nodeWidth: 300, nodeHeight: 88, baseY: 60 },
        team: { nodeWidth: 268, nodeHeight: 76, baseY: 220 },
        individual: { nodeWidth: 240, nodeHeight: 68, baseY: 380 },
      };
      const spacing = 72;
      const maxPerRow = 5;

      for (const level of levels) {
        const levelGoals = filteredGoals.filter(g => g.level === level);
        const config = levelConfig[level];
        levelGoals.forEach((goal, idx) => {
          const row = Math.floor(idx / maxPerRow);
          const col = idx % maxPerRow;
          const goalsInRow = Math.min(maxPerRow, levelGoals.length - row * maxPerRow);
          const totalW = goalsInRow * config.nodeWidth + (goalsInRow - 1) * spacing;
          const startX = Math.max(20, (containerWidth - totalW) / 2);
          posMap.set(goal.id, {
            x: startX + col * (config.nodeWidth + spacing),
            y: config.baseY + row * 120,
            width: config.nodeWidth,
            height: config.nodeHeight,
          });
        });
      }
      return posMap;
    }

    // Tree-based layout: position nodes hierarchically
    const containerWidth = 1100;
    const nodeWidths = { company: 300, team: 260, individual: 230 };
    const nodeHeights = { company: 88, team: 76, individual: 68 };
    const hGap = 48;
    const vGap = 80;
    const currentY = 40;

    // For each top-level child of virtual root (company goals or orphans)
    const topChildren = treeData.children.filter(c =>
      c.goal && c.goal.id !== 'virtual-root' && isGoalVisible(c.goal)
    );

    // Calculate subtree width recursively
    const subtreeWidth = (node: GoalTreeView): number => {
      const goal = node.goal;
      if (!goal) return 0;
      const level = mapBackendLevelToFrontend(goal.level as string | number);
      const w = nodeWidths[level] || 240;
      if (!expandedNodes.has(goal.id!) || !node.children?.length) return w;
      const visibleChildren = node.children.filter(c => c.goal && isGoalVisible(c.goal));
      if (visibleChildren.length === 0) return w;
      const childWidths = visibleChildren.map(c => subtreeWidth(c));
      const totalChildWidth = childWidths.reduce((s, cw) => s + cw, 0) + (visibleChildren.length - 1) * hGap;
      return Math.max(w, totalChildWidth);
    };

    // Recursively position nodes
    const positionNode = (node: GoalTreeView, centerX: number, y: number) => {
      const goal = node.goal;
      if (!goal || !goal.id) return;
      const level = mapBackendLevelToFrontend(goal.level as string | number);
      const w = nodeWidths[level] || 240;
      const h = nodeHeights[level] || 68;
      posMap.set(goal.id, { x: centerX - w / 2, y, width: w, height: h });

      if (!expandedNodes.has(goal.id) || !node.children?.length) return;
      const visibleChildren = node.children.filter(c => c.goal && isGoalVisible(c.goal));
      if (visibleChildren.length === 0) return;

      const childWidths = visibleChildren.map(c => subtreeWidth(c));
      const totalChildWidth = childWidths.reduce((s, cw) => s + cw, 0) + (visibleChildren.length - 1) * hGap;
      let childX = centerX - totalChildWidth / 2;
      const childY = y + h + vGap;

      visibleChildren.forEach((child, i) => {
        const cw = childWidths[i];
        positionNode(child, childX + cw / 2, childY);
        childX += cw + hGap;
      });
    };

    // Layout top-level nodes
    const topWidths = topChildren.map(c => subtreeWidth(c));
    const totalTopWidth = topWidths.reduce((s, w) => s + w, 0) + (topChildren.length - 1) * hGap;
    let startX = Math.max(40, (containerWidth - totalTopWidth) / 2);

    topChildren.forEach((child, i) => {
      const w = topWidths[i];
      positionNode(child, startX + w / 2, currentY);
      startX += w + hGap;
    });

    return posMap;
  };

  // Virtual scrolling
  const loadMoreGoals = (level: string) => {
    setVisibleCounts(prev => ({
      ...prev,
      [level]: (prev[level] || MAX_VISIBLE_PER_SECTION) + MAX_VISIBLE_PER_SECTION,
    }));
  };

  // ----------------------
  // EVENT HANDLERS
  // ----------------------

  const handleGoalClick = async (goal: Goal): Promise<void> => {
    try {
      setLoading(true);
      try {
        const goalDetails = await getGoalById(goal.id);
        const convertedGoal = convertGoalViewToGoal(goalDetails);
        setSelectedGoal(convertedGoal);
      } catch (err) {
        console.error('Could not fetch goal details, using existing data:', err);
        setSelectedGoal(goal);
      }
      setModalMode('view');
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching goal details:', err);
      setError(getErrorMessage(err, t('Failed to fetch goal details')));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewGoal = (): void => {
    setSelectedGoal(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleSaveGoal = async (goalData: Goal, isNew: boolean): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Use multiple fallbacks for userId
      const effectiveUserId = goalData.ownerId || currentUserId || userId || localStorage.getItem('currentUserId');
      if (!effectiveUserId) {
        console.error('User ID not found in any source!');
        setError(t('User session expired. Please refresh the page and try again.'));
        setLoading(false);
        return;
      }

      const goalInput = convertGoalToGoalInput(goalData, effectiveUserId);

      if (isNew) {
        try {
          const newGoal = await createGoal(goalInput);
          const convertedGoal = convertGoalViewToGoal(newGoal);
          setGoals(prev => [...prev, convertedGoal]);
          setSuccessMessage(t('Goal created successfully!'));
          setIsModalOpen(false);
          setSelectedGoal(null);
          // Trigger refetch to get latest data
          setRefetchTrigger(prev => prev + 1);
        } catch (createError) {
          console.error('Failed to create goal:', createError instanceof Error ? createError.message : createError);

          // Handle 403 specifically
          if (getHttpStatus(createError) === 403) {
            setError(t('You do not have permission to create goals for other users. Please select yourself as the owner or contact your administrator.'));
          } else {
            setError(getErrorMessage(createError, t('Failed to create goal')));
          }
          throw createError;
        }
      } else {
        if (!goalData.id) {
          throw new Error(t('Goal ID is required for update'));
        }
        try {
          const updatedGoal = await updateGoal(goalData.id, goalInput);
          const convertedGoal = convertGoalViewToGoal(updatedGoal);
          setGoals(prev => prev.map(g => g.id === goalData.id ? convertedGoal : g));
          setSelectedGoal(convertedGoal);
          setSuccessMessage(t('Goal updated successfully!'));
          setIsModalOpen(false);
        } catch (updateError) {
          console.error('Failed to update goal:', updateError instanceof Error ? updateError.message : updateError);
          setError(getErrorMessage(updateError, t('Failed to update goal')));
          throw updateError;
        }
      }

      // Trigger refetch to get fresh data from server
      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error saving goal:', err);
      setError(getErrorMessage(err, isNew ? t('Failed to create goal') : t('Failed to update goal')));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (goalId: string): Promise<void> => {
    try {
      setLoading(true);
      try {
        const success = await deleteGoal(goalId);
        if (success) {
          setGoals(prev => prev.filter(g => g.id !== goalId));
          setSuccessMessage(t('Goal deleted successfully!'));
        } else {
          throw new Error(t('Failed to delete goal'));
        }
      } catch (deleteError) {
        console.error('API delete failed, deleting locally:', deleteError);
        setGoals(prev => prev.filter(g => g.id !== goalId));
        setSuccessMessage(t('Goal deleted (local demo)'));
      }

      setIsModalOpen(false);
      setSelectedGoal(null);
      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError(getErrorMessage(err, t('Failed to delete goal')));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProgress = async (goalId: string, progress: number, notes?: string): Promise<void> => {
    try {
      const input = {
        goalId,
        progress,
        notes: notes || '',
        updatedAt: new Date().toISOString(),
      };

      try {
        await updateGoalProgress(input);
        setSuccessMessage(t('Progress updated successfully!'));
      } catch (progressError) {
        console.error('API progress update failed, updating locally:', progressError);
        setSuccessMessage(t('Progress updated (local demo)'));
      }

      setGoals(prev => prev.map(g =>
        g.id === goalId ? { ...g, progress } : g
      ));

      if (selectedGoal?.id === goalId) {
        setSelectedGoal(prev => prev ? { ...prev, progress } : null);
      }

      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error updating progress:', err);
      setError(getErrorMessage(err, t('Failed to update progress')));
    }
  };

  const handleAddComment = async (goalId: string, commentText: string): Promise<void> => {
    try {
      setGoals(prev => prev.map(g => {
        if (g.id === goalId) {
          const newComment = {
            id: Date.now(),
            text: commentText,
            author: t('Current User'),
            authorId: 'current-user-id',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          return {
            ...g,
            comments: [...(g.comments || []), newComment]
          };
        }
        return g;
      }));

      if (selectedGoal?.id === goalId) {
        setSelectedGoal(prev => {
          if (!prev) return null;
          const newComment = {
            id: Date.now(),
            text: commentText,
            author: t('Current User'),
            authorId: 'current-user-id',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          return {
            ...prev,
            comments: [...(prev.comments || []), newComment]
          };
        });
      }

      setSuccessMessage(t('Comment added successfully!'));
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(getErrorMessage(err, t('Failed to add comment')));
    }
  };

  const handleAddMilestone = async (goalId: string, milestone: { title: string; dueDate: string }): Promise<void> => {
    try {
      setGoals(prev => prev.map(g => {
        if (g.id === goalId) {
          const newMilestone = {
            id: Date.now(),
            title: milestone.title,
            completed: false,
            dueDate: milestone.dueDate,
          };
          return {
            ...g,
            milestones: [...g.milestones, newMilestone]
          };
        }
        return g;
      }));

      if (selectedGoal?.id === goalId) {
        setSelectedGoal(prev => {
          if (!prev) return null;
          const newMilestone = {
            id: Date.now(),
            title: milestone.title,
            completed: false,
            dueDate: milestone.dueDate,
          };
          return {
            ...prev,
            milestones: [...prev.milestones, newMilestone]
          };
        });
      }

      setSuccessMessage(t('Milestone added successfully!'));
    } catch (err) {
      console.error('Error adding milestone:', err);
      setError(getErrorMessage(err, t('Failed to add milestone')));
    }
  };

  const handleUpdateMilestone = async (milestoneId: number, completed: boolean): Promise<void> => {
    try {
      setGoals(prev => prev.map(g => ({
        ...g,
        milestones: g.milestones.map(m =>
          m.id === milestoneId ? { ...m, completed } : m
        )
      })));

      if (selectedGoal) {
        setSelectedGoal(prev => {
          if (!prev) return null;
          return {
            ...prev,
            milestones: prev.milestones.map(m =>
              m.id === milestoneId ? { ...m, completed } : m
            )
          };
        });
      }

      setSuccessMessage(t('Milestone updated successfully!'));
    } catch (err) {
      console.error('Error updating milestone:', err);
      setError(getErrorMessage(err, t('Failed to update milestone')));
    }
  };

  const handleCloneGoal = async (goalId: string): Promise<void> => {
    try {
      setLoading(true);
      const goalToClone = goals.find(g => g.id === goalId);
      if (!goalToClone) {
        throw new Error(t('Goal not found'));
      }

      const clonedGoal: Goal = {
        ...goalToClone,
        id: `clone-${Date.now()}`,
        title: t('{{title}} (Copy)', { title: goalToClone.title }),
        progress: 0,
        status: 'on-track' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setGoals(prev => [...prev, clonedGoal]);
      setSuccessMessage(t('Goal cloned successfully!'));
    } catch (err) {
      console.error('Error cloning goal:', err);
      setError(getErrorMessage(err, t('Failed to clone goal')));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArchive = async (goalId: string, archive: boolean): Promise<void> => {
    try {
      setLoading(true);
      setGoals(prev => prev.map(g =>
        g.id === goalId ? { ...g, isArchived: archive } : g
      ));

      if (selectedGoal?.id === goalId) {
        setSelectedGoal(prev => prev ? { ...prev, isArchived: archive } : null);
      }

      setSuccessMessage(archive ? t('Goal archived successfully!') : t('Goal unarchived successfully!'));
    } catch (err) {
      console.error('Error toggling archive:', err);
      setError(getErrorMessage(err, t('Failed to update goal archive status')));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Filters): void => {
    setFilters(newFilters);
  };

  const handleToggleSection = (section: keyof ExpandedSections, isExpanded: boolean): void => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const handleViewToggle = (): void => {
    setViewType(prev => prev === 'map' ? 'list' : 'map');
  };

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  // ----------------------
  // RETURN
  // ----------------------

  return {
    // State
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    selectedGoal,
    isModalOpen,
    setIsModalOpen,
    modalMode,
    hoveredGoal,
    setHoveredGoal,
    expandedSections,
    viewType,
    filters,
    goals,
    expandedNodes,
    loading,
    stats,
    error,
    successMessage,
    departments,
    loadingDepartments,
    availableOwners,
    loadingOwners,
    visibleCounts,

    // Computed
    filteredGoals,

    // Functions
    getStatusLabel,
    buildTreePositionMap,
    toggleNodeExpanded,
    loadMoreGoals,

    // Handlers
    handleGoalClick,
    handleCreateNewGoal,
    handleSaveGoal,
    handleDeleteGoal,
    handleUpdateProgress,
    handleAddComment,
    handleAddMilestone,
    handleUpdateMilestone,
    handleCloneGoal,
    handleToggleArchive,
    handleFilterChange,
    handleToggleSection,
    handleViewToggle,
    handleCloseError,
    handleCloseSuccess,
  };
}
